/**
 * Design D's opening page (MATT GO 2026-10-23 00:56; spec magia-mailbox/to-cc.md "2026-10-23 00:58"): what decides
 * whether it shows, and the date-driven deadline line. Pure and tiny: the start screen's static code imports it (the
 * page itself, ./opening.ts, is a lazy chunk).
 *
 * Storage: the opening page replaces the old first-visit welcome (src/ui/welcome.ts) in design D and keeps its browser
 * storage key, so a visitor who already chose "Don't show this again" there is not shown the opening page either, and
 * nothing new is stored (the privacy page lists it: page.privacy.storedLanguage). "Start" writes it.
 */
import CLAIM_WINDOW from '../../params/form106ClaimWindow.json';

export const OPENING_SEEN_KEY = 'magia-li.welcome';
const SEEN = 'dismissed';

/** True unless this browser has already pressed "Start" on the opening page (or dismissed the old welcome). Blocked storage: shown. */
export function shouldShowOpening(storage: () => Pick<Storage, 'getItem'> = () => localStorage): boolean {
  try {
    return storage().getItem(OPENING_SEEN_KEY) !== SEEN;
  } catch {
    return true;
  }
}

/** "Start" pressed: later visits open on the start screen. Blocked storage: the opening page simply shows again next time. */
export function rememberOpening(storage: () => Pick<Storage, 'setItem'> = () => localStorage): void {
  try {
    storage().setItem(OPENING_SEEN_KEY, SEEN);
  } catch {
    // Not essential (private mode etc.).
  }
}

/** The refund window in years (src/params/form106ClaimWindow.json, the entry in force: no end date); tested against src/params. */
const YEARS_AFTER_TAX_YEAR = CLAIM_WINDOW.entries.reduce((years, e) => (e.to === null ? e.value.yearsAfterTaxYear : years), 0);

/** The countdown shows only in the last this-many days of the year (Matt, 2026-10-23 11:10). */
export const COUNTDOWN_DAYS = 120;

/** Year, month (1-12) and day in Israel (Asia/Jerusalem) at `now`. */
function israelDate(now: Date): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Jerusalem', year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(now);
  const n = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { year: n('year'), month: n('month'), day: n('day') };
}

/** The date in Israel (Asia/Jerusalem) at `now`, as an ISO date (YYYY-MM-DD): what the claim time limits count from. */
export function israelIsoDate(now: Date): string {
  const { year, month, day } = israelDate(now);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** The calendar year in Israel (Asia/Jerusalem) at `now`. */
export function israelYear(now: Date): number {
  return israelDate(now).year;
}

/**
 * The deadline line's two years (Income Tax Ordinance s.160(a): a refund return no later than the end of the sixth year
 * after the tax year; the window is the param form106ClaimWindow): in Israel's year Y, the oldest tax year still open is
 * Y - 6, and its last day is 31 December Y.
 */
export function deadlineYears(now: Date): { taxYear: number; year: number; years: number } {
  const year = israelYear(now);
  return { taxYear: year - YEARS_AFTER_TAX_YEAR, year, years: YEARS_AFTER_TAX_YEAR };
}

/** Whole days from today (Israel's date) to 31 December of Israel's year: 0 on 31 December itself. */
export function daysLeft(now: Date): number {
  const { year, month, day } = israelDate(now);
  return Math.round((Date.UTC(year, 11, 31) - Date.UTC(year, month - 1, day)) / 86_400_000);
}

/** The days left to show, or undefined outside the last COUNTDOWN_DAYS days of the year (0 = the last day). */
export function countdownDays(now: Date): number | undefined {
  const days = daysLeft(now);
  return days <= COUNTDOWN_DAYS ? days : undefined;
}
